import { Dispatch, SetStateAction } from 'react';
import {
  IOOSProduct,
  IProduct,
  IShoppingCart,
  ShoppingCartProductType,
  IOrderItem,
  ProductPricingFeatures,
} from '@monofront/types';
import { IFirebaseShoppingCartProducts } from '@monofront/types';
import { ProductService } from '../product';
import { computeShoppingCart } from './computeShoppingCart';
import { Channel } from '../../constants';
import {
  collection,
  doc,
  addDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  increment,
  deleteField,
  Firestore,
} from 'firebase/firestore';
import type {} from '@react-native-firebase/firestore';
import { mergeShoppingCarts } from './mergeShoppingCarts';
import { logger } from '@monofront/logging';
import { CartUpdateStatus, getCartUpdateStatus } from './cartUpdates';

const SHOPPING_CART_DB_COLLECTION = 'carts';

type IProductKey = {
  variantId?: string;
  objectID?: string;
} & {
  sku: string;
};

export class ShoppingCartService {
  public productService: ProductService;

  constructor(
    private firebaseService: { db: Firestore },
    productService: ProductService,
  ) {
    this.productService = productService;
  }

  private getFirebaseShoppingCartProductKey = (product: IProductKey) => {
    return product.sku;
  };

  public async createShoppingCart() {
    const docRef = await addDoc(
      collection(this.firebaseService.db, SHOPPING_CART_DB_COLLECTION),
      {},
    );

    return docRef.id;
  }

  public shoppingCartIdExistsInDb = async (
    shoppingCartId: string,
  ): Promise<boolean> => {
    const docRef = doc(
      this.firebaseService.db,
      SHOPPING_CART_DB_COLLECTION,
      shoppingCartId,
    );
    const docSnap = await getDoc(docRef);

    return docSnap.exists();
  };

  public addProductToShoppingCart = async (
    shoppingCartId: string,
    shoppingCartProduct: ShoppingCartProductType,
  ) => {
    const docRef = doc(
      this.firebaseService.db,
      SHOPPING_CART_DB_COLLECTION,
      shoppingCartId,
    );

    await updateDoc(docRef, {
      [this.getFirebaseShoppingCartProductKey(shoppingCartProduct)]:
        shoppingCartProduct.quantity,
    });
  };

  public addProductsToShoppingCart = async (
    shoppingCartId: string,
    shoppingCartProducts: readonly (ShoppingCartProductType | IOrderItem)[],
  ) => {
    const productsToAdd: IFirebaseShoppingCartProducts =
      shoppingCartProducts.reduce((accumulator, currentProduct) => {
        return {
          ...accumulator,
          [this.getFirebaseShoppingCartProductKey(currentProduct)]:
            currentProduct.quantity || 1,
        };
      }, {});

    const docRef = doc(
      this.firebaseService.db,
      SHOPPING_CART_DB_COLLECTION,
      shoppingCartId,
    );

    await updateDoc(docRef, productsToAdd);
  };

  public addProductsToShoppingCartBySku = async (
    shoppingCartId: string,
    shoppingCartProducts: readonly (ShoppingCartProductType | IOrderItem)[],
    channel: Channel = Channel.FR,
  ) => {
    const products = await this.productService.getProductsBySku(
      shoppingCartProducts.map(({ sku }) => sku),
      channel,
    );

    const productsToAdd: IFirebaseShoppingCartProducts =
      shoppingCartProducts.reduce((accumulator, currentProduct) => {
        const algoliaProduct = products.find(
          ({ sku }) => sku === currentProduct.sku,
        );

        if (!algoliaProduct) {
          return accumulator;
        }

        return {
          ...accumulator,
          [this.getFirebaseShoppingCartProductKey(algoliaProduct)]:
            currentProduct.quantity || 1,
        };
      }, {});

    const docRef = doc(
      this.firebaseService.db,
      SHOPPING_CART_DB_COLLECTION,
      shoppingCartId,
    );

    await updateDoc(docRef, productsToAdd);
  };

  public updateProductInShoppingCart = async (
    shoppingCartId: string,
    shoppingCartProduct: ShoppingCartProductType,
  ) => {
    const docRef = doc(
      this.firebaseService.db,
      SHOPPING_CART_DB_COLLECTION,
      shoppingCartId,
    );

    await updateDoc(docRef, {
      [this.getFirebaseShoppingCartProductKey(shoppingCartProduct)]:
        shoppingCartProduct.quantity,
    });
  };

  public decrementProductInShoppingCart = async (
    shoppingCartId: string,
    shoppingCartProduct: ShoppingCartProductType,
  ) => {
    const quantityToDecrement = shoppingCartProduct.quantity
      ? -shoppingCartProduct.quantity
      : -1;

    const docRef = doc(
      this.firebaseService.db,
      SHOPPING_CART_DB_COLLECTION,
      shoppingCartId,
    );

    await updateDoc(docRef, {
      [this.getFirebaseShoppingCartProductKey(shoppingCartProduct)]:
        increment(quantityToDecrement),
    });
  };

  public deleteProductsFromShoppingCart = async (
    shoppingCartId: string,
    products: Array<{ sku: string }>,
  ) => {
    const dbProducts = products.reduce((accumulator, current) => {
      return {
        ...accumulator,
        [this.getFirebaseShoppingCartProductKey(current)]: deleteField(),
      };
    }, {});

    const docRef = doc(
      this.firebaseService.db,
      SHOPPING_CART_DB_COLLECTION,
      shoppingCartId,
    );

    return await updateDoc(docRef, dbProducts);
  };

  public deleteProductsKeyFromShoppingCart = async (
    shoppingCartId: string,
    productKeys: string[],
  ) => {
    const dbProducts = productKeys.reduce(
      (accumulator, current) => ({
        ...accumulator,
        [current]: deleteField(),
      }),
      {},
    );

    const docRef = doc(
      this.firebaseService.db,
      SHOPPING_CART_DB_COLLECTION,
      shoppingCartId,
    );

    return await updateDoc(docRef, dbProducts);
  };

  public async getDbProducts(shoppingCartId: string) {
    const docRef = doc(
      this.firebaseService.db,
      SHOPPING_CART_DB_COLLECTION,
      shoppingCartId,
    );
    const docSnap = await getDoc(docRef);

    return docSnap.data() as IFirebaseShoppingCartProducts;
  }

  public async getProductsFromSearchService(
    firebaseShoppingCartProducts: IFirebaseShoppingCartProducts,
    shoppingCartId: string,
    channel: Channel,
    features: ProductPricingFeatures,
    skipProductCache = false,
  ) {
    const hasOldFormatKeys = Object.keys(firebaseShoppingCartProducts).some(
      (key) => key.includes('|'),
    );

    if (hasOldFormatKeys) {
      logger.info('Emptying shopping cart with old format');

      await this.deleteProductsKeyFromShoppingCart(
        shoppingCartId,
        Object.keys(firebaseShoppingCartProducts),
      );

      return computeShoppingCart(
        [],
        {},
        this.getFirebaseShoppingCartProductKey,
        shoppingCartId,
        features,
      );
    }

    const formattedDbProducts = firebaseShoppingCartProducts;

    const algoliaProducts: IProduct[] =
      await this.productService.getProductsBySku(
        // Sorting is important because Firestore does not guarantee the properties order
        // of its documents (if you check, the products will be in random order every time).
        // Having the keys in a consistent order ensure we hit the Algolia's requests cache.
        Object.keys(formattedDbProducts).sort(),
        channel,
        skipProductCache,
      );

    const algoliaProductsWithReplacements: Array<IProduct | IOOSProduct> =
      await Promise.all(
        algoliaProducts.map(async (product: IProduct) => {
          const firebaseProductQuantity =
            firebaseShoppingCartProducts[
              this.getFirebaseShoppingCartProductKey(product)
            ];

          const isPartialProduct =
            product.inventory_quantity > 0 &&
            product.inventory_quantity < firebaseProductQuantity;
          const isOOS = product.inventory_quantity <= 0;

          if (isPartialProduct || isOOS) {
            const collection =
              product.breadcrumb &&
              product.breadcrumb[product.breadcrumb.length - 1]?.handle;

            const replacementProduct =
              await this.productService.getReplacementProduct(
                product.sku,
                product.title,
                isPartialProduct
                  ? firebaseProductQuantity
                  : product.inventory_quantity,
                collection,
                channel,
              );

            if (replacementProduct) {
              const productWithReplacement: IOOSProduct = {
                ...product,
                replacementProduct,
              };

              return productWithReplacement;
            }
          }

          return product;
        }),
      );

    const shoppingCart: IShoppingCart = computeShoppingCart(
      algoliaProductsWithReplacements,
      formattedDbProducts,
      this.getFirebaseShoppingCartProductKey,
      shoppingCartId,
      features,
    );

    const allProducts = [...shoppingCart.products, ...shoppingCart.oosProducts];

    const shoppingCartProductSkus = allProducts.map((product) => product.sku);

    const notFoundProducts = Object.keys(formattedDbProducts).filter(
      (key) => !shoppingCartProductSkus.some((sku) => key.includes(sku)),
    );

    if (notFoundProducts.length > 0) {
      await this.deleteProductsKeyFromShoppingCart(
        shoppingCartId,
        notFoundProducts,
      );
    }

    return shoppingCart;
  }

  public listenToShoppingCart = (
    shoppingCartId: string,
    channel: Channel = Channel.FR,
    updateShoppingCartState: (
      cartUpdateStatus: CartUpdateStatus,
      newShoppingCart: IShoppingCart,
    ) => void,
    setIsShoppingCartDeleted: Dispatch<SetStateAction<boolean>> = () => 0,
    features: Omit<ProductPricingFeatures, 'bundleSaleRate'>,
  ) => {
    const docRef = doc(
      this.firebaseService.db,
      SHOPPING_CART_DB_COLLECTION,
      shoppingCartId,
    );

    let previousCart: IShoppingCart | null = null;

    return onSnapshot(docRef, async (snapshot) => {
      const firebaseShoppingCartProducts: IFirebaseShoppingCartProducts =
        snapshot.data()!;

      if (!firebaseShoppingCartProducts) {
        setIsShoppingCartDeleted(true);

        return;
      }

      const shoppingCart = await this.getProductsFromSearchService(
        firebaseShoppingCartProducts,
        shoppingCartId,
        channel,
        features,
      );

      const cartUpdateStatus = getCartUpdateStatus(shoppingCart, previousCart);

      updateShoppingCartState(cartUpdateStatus, shoppingCart);

      previousCart = shoppingCart;
    });
  };

  public async delete(shoppingCartId: string) {
    const docRef = doc(
      this.firebaseService.db,
      SHOPPING_CART_DB_COLLECTION,
      shoppingCartId,
    );

    await deleteDoc(docRef);
  }

  public async mergeLocalAndCustomerShoppingCartsInDb(
    localShoppingCartId: string,
    customerShoppingCartId: string,
  ) {
    const [localShoppingCart, customerShoppingCart] = await Promise.all([
      this.getDbProducts(localShoppingCartId),
      this.getDbProducts(customerShoppingCartId),
    ]);

    const mergedShoppingCarts = mergeShoppingCarts(
      localShoppingCart!,
      customerShoppingCart!,
    );

    const docRef = doc(
      this.firebaseService.db,
      SHOPPING_CART_DB_COLLECTION,
      customerShoppingCartId,
    );

    await updateDoc(docRef, mergedShoppingCarts);

    await this.delete(localShoppingCartId);
  }
}
